import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '../tokens';
import Button from '@mui/material/Button';
import HowToRegIcon from '@mui/icons-material/HowToReg';

const useStyles = makeStyles()(theme => {
  const colors = getColors(theme.palette.mode);
  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      margin: '24px 48px',
      padding: '16px 24px',
      background: colors.background.paper,
      border: `1px solid ${colors.border.default}`,
      borderRadius: 10,
    },
    icon: {
      flexShrink: 0,
      fontSize: '3rem',
      fill: 'url(#contributeBannerGradient)',
    },
    text: {
      flex: 1,
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: colors.text.primary,
      margin: 0,
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.8rem',
      color: colors.text.secondary,
      margin: '4px 0 0',
    },
    button: {
      background: colors.primary.main,
      border: 'none',
      borderRadius: 20,
      color: '#FFFFFF',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.85rem',
      fontWeight: 600,
      padding: '10px 20px',
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
      '&:hover': {
        background: '#006CBD',
      },
    },
  };
});

export interface ContributeBannerProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  href?: string;
}

export const ContributeBanner = ({
  title = 'Contribute to ODP',
  description = 'Learn how to collaborate with the ODP community using our developer documentation.',
  buttonLabel = 'Start contributing',
  href = '/getting-started',
}: Readonly<ContributeBannerProps>) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <svg
        width="0"
        height="0"
        style={{ position: 'absolute' }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="contributeBannerGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#0078D6" />
            <stop offset="100%" stopColor="#17E3CC" />
          </linearGradient>
        </defs>
      </svg>
      <HowToRegIcon className={classes.icon} />
      <div className={classes.text}>
        <p className={classes.title}>{title}</p>
        <p className={classes.description}>{description}</p>
      </div>
      <Button className={classes.button} onClick={() => navigate(href)}>
        {buttonLabel}
      </Button>
    </div>
  );
};
